import React, {useState} from "react";
import {AccountContext} from "../../context/account/AccountContext";
import {useCategoryParams} from "../../hooks/useCategoryParams";
import {useCategoryStreams, CategoryStream} from "../../hooks/useCategoryStreams";
import styles from "../Category/categories.module.css";
import Loading from "../common/Loading";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimes} from "@fortawesome/free-solid-svg-icons";
import SeriesEpisodes from "./SeriesEpisodes";

interface SeriesListProps {
    onClose?: () => void;
}

const SeriesList: React.FC<SeriesListProps> = ({ onClose }) => {
    const { data: accountData } = React.useContext(AccountContext);
    const [params, actions] = useCategoryParams();
    const [selectedSeriesName, setSelectedSeriesName] = useState<string>('');

    const { data: series, isLoading, error } = useCategoryStreams({
        baseUrl: accountData?.url || '',
        username: accountData?.username || '',
        password: accountData?.password || '',
        categoryId: params.categoryId,
        streamType: 'get_series'
    });

    if (!accountData) {
        return <div className={styles.error}>Ingen kontoinformasjon tilgjengelig</div>;
    }

    if (!params.categoryId) {
        return <div className={styles.empty}>Velg en kategori for å se kanaler</div>;
    }

    if (isLoading) {
        return <Loading />;
    }

    if (error) {
        return <div className={styles.error}>Feil ved lasting av kanaler: {error.message}</div>;
    }

    if (!series || series.length === 0) {
        return <div className={styles.empty}>Ingen serier funnet i denne kategorien</div>;
    }

    // If a series is selected (from URL), show its episodes
    if (params.seriesId) {
        return (
            <SeriesEpisodes
                seriesId={params.seriesId}
                seriesName={selectedSeriesName}
                onBack={() => {
                    actions.clearSeriesId();
                    setSelectedSeriesName('');
                }}
            />
        );
    }

    const handleSeriesClick = (seriesItem: CategoryStream) => {
        if (seriesItem.series_id) {
            actions.setSeriesId(seriesItem.series_id.toString());
        }
        setSelectedSeriesName(seriesItem.name);
    };

    return (
        <div className={styles.categoriesContainer}>
            <div className={styles.streamsHeader}>
                <h2 className={styles.title}>Serier ({series.length})</h2>
                {onClose && (
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        title="Lukk serieliste"
                        aria-label="Lukk serieliste"
                    >
                        <FontAwesomeIcon icon={faTimes}/>
                    </button>
                )}
            </div>
            <ul className={styles.streamsList}>
                {series.map((seriesItem) => (
                    <li
                        key={seriesItem.num || seriesItem.stream_id}
                        className={styles.streamItem}
                        onClick={() => handleSeriesClick(seriesItem)}
                    >
                        <div className={styles.streamInfo}>
                            <span className={styles.streamName}>{seriesItem.name}</span>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default SeriesList;